import React, { useMemo } from "react"

import { PoundSterling } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer } from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { DateFilter } from "@/types/filters"
import { COLORS } from "@/utils/chartColors"

import { useCostTrackingData } from "../../../hooks/useCostTrackingData"
import { MetricCard } from "../MetricCard"

interface CostsTabProps {
  dateFilter?: DateFilter
  jobId?: string
}

const CostsTab: React.FC<CostsTabProps> = ({ dateFilter, jobId }) => {
  // Fetch cost tracking data
  const { data: costData, isLoading, error } = useCostTrackingData(dateFilter, jobId)

  // Prepare data for pie chart
  const pieChartData = useMemo(() => {
    if (!costData) return []

    return [
      {
        name: "DVLA Checks",
        value: costData.costBreakdown.dvla.current,
        fill: COLORS[0],
      },
      {
        name: "RTW Checks",
        value: costData.costBreakdown.rtw.current,
        fill: COLORS[1],
      },
      {
        name: "Email Sends",
        value: costData.costBreakdown.email.current,
        fill: COLORS[2],
      },
      {
        name: "Other",
        value: costData.costBreakdown.other.current,
        fill: COLORS[3],
      },
    ].filter((item) => item.value > 0) // Only include items with values
  }, [costData])

  // Prepare data for bar chart showing costs by type over time
  const barChartData = useMemo(() => {
    if (!costData || !costData.costsByType) return []

    return Object.entries(costData.costsByType).map(([type, data]) => ({
      name: type.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      current: data.current,
      previous: data.previous,
    }))
  }, [costData])

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-1/4" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-80" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 border border-red-300 bg-red-50 rounded-md">
        <h3 className="text-lg font-semibold text-red-800">Error loading cost data</h3>
        <p className="text-red-600">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Summary metrics cards */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard
          title="Total Costs"
          value={`£${costData?.totalCosts.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.totalCosts.percentChange}
          previousValue={`£${costData?.totalCosts.previous.toFixed(2) || "0.00"}`}
          description="Includes base cost per application"
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
        <MetricCard
          title="Avg. Cost Per Application"
          value={`£${costData?.averageCostPerApplication.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.averageCostPerApplication.percentChange}
          previousValue={`£${costData?.averageCostPerApplication.previous.toFixed(2) || "0.00"}`}
          description="Cost per application"
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="DVLA Check Costs"
          value={`£${costData?.costBreakdown.dvla.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.costBreakdown.dvla.percentChange}
          previousValue={`£${costData?.costBreakdown.dvla.previous.toFixed(2) || "0.00"}`}
          description={`£0.60 per successful check`}
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
        <MetricCard
          title="RTW Check Costs"
          value={`£${costData?.costBreakdown.rtw.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.costBreakdown.rtw.percentChange}
          previousValue={`£${costData?.costBreakdown.rtw.previous.toFixed(2) || "0.00"}`}
          description={`£0.20 per check`}
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
        <MetricCard
          title="Email Costs"
          value={`£${costData?.costBreakdown.email.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.costBreakdown.email.percentChange}
          previousValue={`£${costData?.costBreakdown.email.previous.toFixed(2) || "0.00"}`}
          description={`£0.01 per email`}
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
        <MetricCard
          title="Other Costs"
          value={`£${costData?.costBreakdown.other.current.toFixed(2) || "0.00"}`}
          percentChange={costData?.costBreakdown.other.percentChange}
          previousValue={`£${costData?.costBreakdown.other.previous.toFixed(2) || "0.00"}`}
          description="Miscellaneous costs"
          icon={<PoundSterling size={24} />}
          reverse={true}
        />
      </div>

      {/* Cost breakdown pie chart */}
      <Card>
        <CardHeader>
          <CardTitle>Cost Distribution</CardTitle>
          <CardDescription>Breakdown of costs by category</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number | string) => `£${Number(value).toFixed(2)}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Costs by type bar chart */}
      <Card>
        <CardHeader>
          <CardTitle>Costs By Type</CardTitle>
          <CardDescription>Current vs previous period</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer
            className="h-80"
            config={{
              current: { color: COLORS[0] },
              previous: { color: COLORS[1] },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value: number) => `£${value.toFixed(2)}`} />
                <Tooltip formatter={(value: number | string) => `£${Number(value).toFixed(2)}`} />
                <Legend />
                <Bar dataKey="current" name="Current Period" fill={COLORS[0]} />
                <Bar dataKey="previous" name="Previous Period" fill={COLORS[1]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Fixed cost breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Fixed Cost Reference</CardTitle>
          <CardDescription>Standard costs for verification actions</CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Base cost per application</p>
              <p className="text-lg font-medium">£1.78</p>
              <p className="text-xs text-muted-foreground">Fixed cost per application</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">DVLA check cost</p>
              <p className="text-lg font-medium">£0.60</p>
              <p className="text-xs text-muted-foreground">Per successful check</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">RTW check cost</p>
              <p className="text-lg font-medium">£0.20</p>
              <p className="text-xs text-muted-foreground">Per check (success or failure)</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email cost</p>
              <p className="text-lg font-medium">£0.01</p>
              <p className="text-xs text-muted-foreground">Per email sent</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CostsTab
